import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { BLACK, FEED_BACK, PRIMARY_PURPLE, RED } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { SAVE_STATUS } from '../constants';

import FieldLabel from './FieldLabel';
import SaveStatusIndicator from './SaveStatusIndicator';

export const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: BLACK[0],
    borderRadius: '6px',
    fontFamily: PRIMARY_FONT[400],
    fontSize: '14px',
    '& fieldset': { borderColor: BLACK[40] },
    '&:hover fieldset': { borderColor: BLACK[60] },
    '&.Mui-focused fieldset': { borderColor: PRIMARY_PURPLE[70] },
    '&.Mui-error fieldset': { borderColor: RED[80] },
  },
};

const helperTextSx = { color: BLACK[60], fontFamily: PRIMARY_FONT[400], fontSize: '12px' };

const errorTextSx = { ...helperTextSx, color: RED[80] };

const characterCountSx = { ...helperTextSx, whiteSpace: 'nowrap' };

const validTextSx = { ...helperTextSx, color: FEED_BACK.GREEN_80 };

const rootSx = { display: 'flex', flexDirection: 'column', gap: '6px' };

const labelRowSx = { alignItems: 'center', display: 'flex', justifyContent: 'space-between' };

const inputRowSx = { alignItems: 'center', display: 'flex', gap: '12px' };

const validIndicatorSx = {
  alignItems: 'center',
  color: FEED_BACK.GREEN_80,
  display: 'flex',
  gap: '4px',
};

const validIconSx = { fontSize: '16px' };

const footerRowSx = { display: 'flex', justifyContent: 'space-between', gap: '16px' };

const AutoSaveTextField = ({
  id,
  label,
  value,
  onSave,
  status,
  helperText,
  validate,
  multiline = false,
  minRows,
  showCharacterCount = false,
  type,
  startAdornment,
  endAdornment,
  inputWidth,
  showValidIndicator = false,
}) => {
  const intl = useIntl();
  const [draft, setDraft] = useState(value ?? '');
  const isFocusedRef = useRef(false);
  const isRevertingRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) setDraft(value ?? '');
  }, [value]);

  const isDirty = draft !== (value ?? '');
  const errorMessage = validate && isDirty ? validate(draft) : null;
  const isValid = draft !== '' && !(validate && validate(draft));
  const inputSx = useMemo(
    () => (inputWidth ? { ...textFieldSx, width: inputWidth } : textFieldSx),
    [inputWidth]
  );

  const commit = () => {
    if (isRevertingRef.current) {
      isRevertingRef.current = false;
      return;
    }
    if (errorMessage || !isDirty) return;
    onSave(draft);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      isRevertingRef.current = true;
      setDraft(value ?? '');
      event.target.blur();
    } else if (event.key === 'Enter' && !multiline) {
      event.target.blur();
    }
  };

  return (
    <Box sx={rootSx}>
      <Box sx={labelRowSx}>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <SaveStatusIndicator status={status} />
      </Box>
      <Box sx={inputRowSx}>
        <TextField
          id={id}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={() => {
            isFocusedRef.current = true;
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            commit();
          }}
          onKeyDown={handleKeyDown}
          error={!!errorMessage || status === SAVE_STATUS.ERROR}
          multiline={multiline}
          minRows={minRows}
          type={type}
          InputProps={{
            startAdornment: startAdornment && (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ),
            endAdornment: endAdornment && (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ),
          }}
          size="small"
          fullWidth={!inputWidth}
          sx={inputSx}
        />
        {showValidIndicator && isValid && (
          <Box data-testid={`${id}-valid`} sx={validIndicatorSx}>
            <CheckIcon sx={validIconSx} />
            <Typography sx={validTextSx}>
              {intl.formatMessage({ id: 'pt.detail.valid' })}
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={footerRowSx}>
        <Typography sx={errorMessage ? errorTextSx : helperTextSx}>
          {errorMessage || helperText}
        </Typography>
        {showCharacterCount && (
          <Typography sx={characterCountSx}>
            {intl.formatMessage({ id: 'pt.detail.characterCount' }, { count: draft.length })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

AutoSaveTextField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  status: PropTypes.oneOf(Object.values(SAVE_STATUS)),
  helperText: PropTypes.string,
  validate: PropTypes.func,
  multiline: PropTypes.bool,
  minRows: PropTypes.number,
  showCharacterCount: PropTypes.bool,
  type: PropTypes.string,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  inputWidth: PropTypes.string,
  showValidIndicator: PropTypes.bool,
};

export default AutoSaveTextField;
